import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useCharvysSalesFlow, type SalesFlowMessage } from "@/components/feature/CharvysSalesFlow";

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
  actions?: ChatAction[];
}

interface ChatAction {
  type: "ticket" | "appointment" | "lead";
  label: string;
  payload?: Record<string, unknown>;
}

interface AiRule {
  id: number;
  site: string;
  scope: string;
  lang: string;
  title: string;
  rule_text: string;
  enabled: boolean;
  priority: number;
}

/* ─── Constants ──────────────────────────────────────────────────────────── */
const WIDGET_ID = "ip-ai-chat-widget";
const STORAGE_KEY = "ip_chat_history_v3";
const MAX_HISTORY = 50;
const MUTE_KEY = "ip_chat_muted";

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function stripNonDigits(v: string): string {
  return v.replace(/\D/g, "");
}

function toWhatsAppUrl(raw: string, msg = "Hola Imagen Printing, necesito ayuda"): string {
  const digits = stripNonDigits(raw);
  const number =
    digits.length === 10 && digits.startsWith("1") === false
      ? `1${digits}`
      : digits;
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
}

function formatTime(d: string) {
  return new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

// Show on all public pages — never on admin, portal, kiosk, or track
function shouldShowWidget(path: string) {
  const hiddenPrefixes = ["/admin", "/sa", "/portal", "/kiosk", "/track"];
  return !hiddenPrefixes.some((p) => path.startsWith(p));
}

// Detect product family from query — extracts slug from known product types
const PRODUCT_FAMILY_PATTERNS: Array<{ pattern: RegExp; slug: string }> = [
  { pattern: /tarjeta|tarjetas|business\s*card|presentaci[oó]n|visiting\s*card/i, slug: "business-cards" },
  { pattern: /flyer|flyers|volante|volantes|panfleto/i, slug: "flyers" },
  { pattern: /banner|banners|lonas?|pend[oó]n/i, slug: "banners" },
  { pattern: /sticker|stickers|calcoman[ií]a|pegatina/i, slug: "stickers" },
  { pattern: /yard\s*sign|letrero|letreros|jard[ií]n/i, slug: "yard-signs" },
  { pattern: /camiseta|camisetas|playera|t-?shirt|tshirt|polo/i, slug: "shirts" },
  { pattern: /dtf|gang\s*sheet|transfer/i, slug: "dtf" },
  { pattern: /poster|posters|p[oó]ster/i, slug: "posters" },
  { pattern: /bandera|banderas|pluma/i, slug: "banderas" },
  { pattern: /canopy|canopies|toldo|toldos/i, slug: "canopies" },
  { pattern: /gorra|gorras|hat|hats/i, slug: "hats" },
];

function detectProductFamily(text: string): string | null {
  const lower = text.toLowerCase();
  for (const entry of PRODUCT_FAMILY_PATTERNS) {
    if (entry.pattern.test(lower)) return entry.slug;
  }
  return null;
}

function isProductQuery(text: string): boolean {
  return detectProductFamily(text) !== null;
}

// Extract a quantity from the visitor's message (e.g. "500 tarjetas", "2 mil flyers")
function detectQuantity(text: string): number | null {
  const lower = text.toLowerCase().replace(/,/g, "");
  const milMatch = lower.match(/(\d{1,4})\s*mil\b/);
  if (milMatch) return parseInt(milMatch[1], 10) * 1000;
  const match = lower.match(/\b(\d{1,6})\b/);
  if (match) {
    const n = parseInt(match[1], 10);
    if (n >= 1 && n <= 100000) return n;
  }
  return null;
}

/* ─── Doorbell Sound ─────────────────────────────────────────────────────── */
function playDoorbell() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    function chime(freq: number, start: number, duration: number, vol: number) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + duration);
    }

    chime(880, now, 0.35, 0.35);
    chime(660, now + 0.35, 0.55, 0.35);
  } catch {
    // Audio not available, silently skip
  }
}

/* ─── Soft Chime — notification for every new message ────────────────────── */
function playSoftChime() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    // Fundamental — gentle bell-like tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.value = 1046.5; // C6
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.12, now + 0.015);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.3);

    // Harmonic for bell richness
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.value = 1568; // G6
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(0.05, now + 0.01);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now);
    osc2.stop(now + 0.2);
  } catch {
    // Audio not available, silently skip
  }
}

/* ─── Contact info extraction ──────────────────────────────────────────── */
function extractContactInfo(text: string) {
  const nameMatch = text.match(/(?:nombre|name|soy|llamo|llamó|mi nombre es)[\s:=]+([^\n,]+)/i);
  const phoneMatch = text.match(/(?:tel[eé]fono|phone|whatsapp|celular|m[oó]vil|n[uú]mero)[\s:=]+([^\n,]+)/i);
  const emailMatch = text.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
  return {
    name: nameMatch?.[1]?.trim() || "",
    phone: phoneMatch?.[1]?.trim() || "",
    email: emailMatch?.[1]?.trim() || "",
  };
}

/* ─── Build conversation history for AI backend ────────────────────────── */
function buildHistory(messages: ChatMessage[]): { role: string; content: string }[] {
  const cleaned = messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .filter((m) => m.content && m.content.trim())
    .filter((m) => !m.content.startsWith("✅"))
    .filter((m, i, arr) => {
      if (i === 0) return true;
      const prev = arr[i - 1];
      return !(prev.role === m.role && prev.content === m.content);
    })
    .slice(-15);

  return cleaned.map((m) => ({ role: m.role, content: m.content }));
}

function extractKnownContext(messages: ChatMessage[]) {
  const allText = messages.map((m) => m.content).join(" \n");
  const lower = allText.toLowerCase();

  const productKnown =
    /tarjeta|tarjetas|flyer|flyers|banner|banners|poster|posters|calendario|calendarios|camiseta|camisetas|taza|tazas|sticker|stickers|magnet|imanes|lona|lonas|rotulo|rotulos|toldo|toldos|vinil|viniles|credencial|credenciales|invitacion|invitaciones|menu|menus|catalogo|catalogos|brochure|brochures/i.test(allText);

  const qtyMatch = allText.match(/(\d{1,6})\s*(?:piezas|unidades|uds|copias|hojas|tarjetas|flyers|banners|posters|camisetas|tazas|stickers|imanes|lonas)/i);
  const quantityKnown = qtyMatch ? qtyMatch[1] : "";

  const hasDesign =
    /tengo\s+(?:diseño|artwork|archivo|archivos|imagen|imagenes|listo|lista|preparado)/i.test(allText) ||
    /s[iíí]/i.test(allText);

  const needsDesign =
    /no\s+tengo|necesito\s+diseño|necesito\s+artwork|diseñar|hacer\s+(?:el\s+)?diseño/i.test(allText);

  const nameMatch = allText.match(/(?:nombre|name|soy|me\s+llamo|llamó|llama)[\s:=]+([A-Za-zÁÉÍÓÚáéíóúñÑ\s]{2,40})/i);
  const nameKnown = nameMatch ? nameMatch[1].trim() : "";

  const phoneMatch = allText.match(/(?:tel[eé]fono|phone|whatsapp|celular|m[oó]vil|n[uú]mero)[\s:=]+([\d\s\-+()]{7,20})/i);
  const phoneKnown = phoneMatch ? phoneMatch[1].trim() : "";

  const dateMatch = allText.match(/(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/);
  const dateKnown = dateMatch ? dateMatch[1] : "";

  const timeMatch = allText.match(/(\d{1,2}:\d{2}\s*(?:am|pm|AM|PM)?)/);
  const timeKnown = timeMatch ? timeMatch[1] : "";

  return {
    product: productKnown ? "known" : "unknown",
    quantity: quantityKnown,
    has_design: hasDesign,
    needs_design: needsDesign,
    name: nameKnown,
    phone: phoneKnown,
    date: dateKnown,
    time: timeKnown,
    full_transcript: allText.slice(-800),
  };
}

/* ─── Charvys Sales Inbox (Unified AI Chat Widget) ──────────────────────── */
function AIChatWidgetInner() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState<AiRule[]>([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [handoffTicketId, setHandoffTicketId] = useState<number | null>(null);
  const [handoffMode, setHandoffMode] = useState(false);
  const [lastAdminMsgId, setLastAdminMsgId] = useState<number>(0);
  const [sendingHandoff, setSendingHandoff] = useState(false);
  const [humanRequested, setHumanRequested] = useState(false);
  const [humanRequestedAt, setHumanRequestedAt] = useState<string | null>(null);
  const [handoffCollectingInfo, setHandoffCollectingInfo] = useState(false);
  const [handoffFormName, setHandoffFormName] = useState("");
  const [handoffFormPhone, setHandoffFormPhone] = useState("");
  const [handoffFormError, setHandoffFormError] = useState("");
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem("ip_chat_session");
    if (existing) return existing;
    const id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("ip_chat_session", id);
    return id;
  });
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === "true"; } catch { return false; }
  });
  const lastSoundIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isHandlingRef = useRef(false);

  // ── Sales Flow Mode ──
  const [salesFlowActive, setSalesFlowActive] = useState(false);
  const [salesFlowTriggered, setSalesFlowTriggered] = useState(false);
  const [detectedProductSlug, setDetectedProductSlug] = useState<string>("business-cards");

  const salesFlow = useCharvysSalesFlow({
    visitorId: sessionId,
    productSlug: detectedProductSlug,
    quantity: 1000,
    onComplete: (data) => {
      console.log("Sales flow completed:", data);
    },
    onMessagesChange: (salesMsgs) => {
      // Messages are managed internally by salesFlow
    },
  });

  // Activate sales flow for a detected product family — only from a real query.
  // Calling this is what actually requests live pricing (via salesFlow.start).
  const activateSalesFlow = useCallback((slug?: string, qty?: number) => {
    if (salesFlowTriggered) return;
    if (slug && slug !== detectedProductSlug) {
      setDetectedProductSlug(slug);
    }
    setSalesFlowTriggered(true);
    setSalesFlowActive(true);
    salesFlow.start(slug, qty ?? undefined);
  }, [salesFlowTriggered, detectedProductSlug, salesFlow]);

  /* ── Mobile viewport detection ─────────────────────────────────────
     ONLY used to switch to fullscreen on mobile (< 768px).
     Desktop is ALWAYS floating panel — never fullscreen. */
  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    const handleResize = () => {
      // Force re-render on resize so mobile/desktop switch works dynamically
      // without needing viewportHeight state
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  /* ── Keep viewportHeight state for the resize listener above ─────── */
  const [, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 9999
  );

  /* ── Toggle mute ──────────────────────────────────────────────────── */
  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem(MUTE_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /* ── Sound on new incoming messages ───────────────────────────────── */
  useEffect(() => {
    if (!messages.length || muted) return;

    // Find the most recent non-user message (assistant / system / admin)
    const newestIncoming = [...messages].reverse().find((m) => m.role !== "user");
    if (!newestIncoming) return;

    // Already sounded for this message?
    if (lastSoundIdRef.current === newestIncoming.id) return;

    // Don't sound while user is composing a message
    const isTyping = input.length > 0 || document.activeElement === inputRef.current;
    if (isTyping) return;

    // Play soft chime and mark as sounded
    playSoftChime();
    lastSoundIdRef.current = newestIncoming.id;
  }, [messages, muted, input]);

  /* ── Load chat history ────────────────────────────────────────────── */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        const migrated = parsed.map((m) =>
          m.role === "ai" ? ({ ...m, role: "assistant" as const }) : m
        );
        setMessages(migrated.slice(-MAX_HISTORY));
        setHasGreeted(migrated.length > 0);
        // Mark last loaded message as already sounded so old messages don't trigger
        const lastMsg = migrated.at(-1);
        if (lastMsg) lastSoundIdRef.current = lastMsg.id;
        // NO restaurar humanRequested desde historial — solo backend decide handoff
      }
    } catch {
      // ignore parse errors
    }
    const saved = sessionStorage.getItem("ip_chat_handoff_ticket");
    if (saved) {
      const tid = parseInt(saved, 10);
      if (!isNaN(tid)) {
        setHandoffTicketId(tid);
        setHandoffMode(true);
        setHumanRequested(true);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
  }, [messages]);

  useEffect(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /* ── addMessage helper ────────────────────────────────────────────── */
  const addMessage = useCallback((msg: ChatMessage) => {
    if (!msg.content || !msg.content.trim()) return;
    setMessages((prev) => [...prev, msg].slice(-MAX_HISTORY));
  }, []);

  /* ── Poll for admin replies in handoff mode (60s, only when widget is open) ── */
  useEffect(() => {
    if (!handoffMode || !handoffTicketId || !open) return;

    const poll = async () => {
      const { data, error } = await supabase
        .from("ip_support_messages")
        .select("id, message, created_at, sender_role")
        .eq("ticket_id", handoffTicketId)
        .eq("sender_role", "admin")
        .gt("id", lastAdminMsgId)
        .order("id", { ascending: true });

      if (error || !data || data.length === 0) return;

      let maxId = lastAdminMsgId;
      let returnDetected = false;

      for (const msg of data) {
        maxId = Math.max(maxId, msg.id);

        // Detect return-to-Charvys signal from admin
        if (
          msg.message &&
          (msg.message.includes("Charvys retomó la conversación") ||
           msg.message.includes("retomó la conversación"))
        ) {
          returnDetected = true;
          // Don't add this as a visible message — it's an internal signal
          continue;
        }

        addMessage({
          id: `admin_${msg.id}`,
          role: "assistant",
          content: msg.message,
          createdAt: msg.created_at,
        });
      }

      setLastAdminMsgId(maxId);

      // Exit handoff mode — return to AI
      if (returnDetected) {
        setHandoffMode(false);
        setHandoffTicketId(null);
        setHumanRequested(false);
        setHumanRequestedAt(null);
        setLastAdminMsgId(0);
        sessionStorage.removeItem("ip_chat_handoff_ticket");

        addMessage({
          id: `return_${Date.now()}`,
          role: "system",
          content: "🤖 Charvys está de vuelta. ¿En qué más te puedo ayudar?",
          createdAt: new Date().toISOString(),
        });

        // Trigger a fresh AI greeting after a short delay
        setTimeout(() => {
          addMessage({
            id: `a_return_${Date.now()}`,
            role: "assistant",
            content: "¡Hola de nuevo! Soy Charvys. El asesor ya revisó tu caso. ¿Necesitas algo más o quieres continuar con tu cotización?",
            createdAt: new Date().toISOString(),
          });
        }, 1500);
      }
    };

    poll();
    const iv = setInterval(poll, 60000);
    return () => clearInterval(iv);
  }, [handoffMode, handoffTicketId, open, lastAdminMsgId, addMessage]);

  /* ── Load AI rules from Supabase ─────────────────────────────────── */
  useEffect(() => {
    const loadRules = async () => {
      const { data } = await supabase
        .from("ai_rules")
        .select("id, site, scope, lang, title, rule_text, enabled, priority")
        .eq("site", "imagenprinting")
        .eq("enabled", true)
        .order("priority", { ascending: false });
      if (data) {
        setRules(data);
        setRulesLoaded(true);
      }
    };
    loadRules();
  }, []);

  /* ── Load business WhatsApp number ────────────────────────────────── */
  useEffect(() => {
    const loadBusinessPhone = async () => {
      try {
        const res = await supabase.functions.invoke("get-business-info", {
          body: {},
        });
        const phone = res.data?.whatsapp || res.data?.phone || "";
        if (phone) {
          setWhatsappUrl(toWhatsAppUrl(String(phone)));
        }
      } catch (err) {
        console.error("Failed to load business phone:", err);
      }
    };
    loadBusinessPhone();
  }, []);

  /* ── Submit lead via edge function ────────────────────────────────── */
  const submitLead = useCallback(async (name: string, phone: string, need: string) => {
    try {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      await supabase.functions.invoke("submit-lead", {
        body: {
          name,
          phone,
          service: need,
          site: "imagenprinting",
          landing_path: pageUrl,
          user_agent: navigator.userAgent,
          form_time: String(Date.now() - 5000),
          form_nonce: btoa(`${Date.now() - 5000}:chat`),
          human_check: "PRINTING",
        },
      });
    } catch (err) {
      console.error("Lead submission error:", err);
    }
  }, []);

  /* ── Save lead directly to marketing_leads (faster, no edge func) ── */
  const saveLeadToDB = useCallback(async (name: string, phone: string, email: string, notes: string) => {
    if (!name.trim() && !phone.trim()) return;
    try {
      await supabase.from("marketing_leads").insert({
        name: name.trim() || "Sales Chat Lead",
        phone: phone.trim() || "N/A",
        email: email.trim() || null,
        notes: notes.slice(0, 500),
        service: "Sales Chat",
        source: "charvys",
        status: "NEW",
        landing_path: window.location.pathname,
        user_agent: navigator.userAgent,
        site: "imagenprinting",
      });
    } catch (err) {
      console.error("Lead DB save error:", err);
    }
  }, []);

  /* ── Pending handoff name/phone ref ───────────────────────────── */
  const pendingHandoffName = useRef("");
  const pendingHandoffPhone = useRef("");

  /* ── Handoff to human ────────────────────────────────────────────── */
  const handoffToHuman = useCallback(async (reason: string) => {
    if (isHandlingRef.current) return;
    isHandlingRef.current = true;

    const now = new Date().toISOString();
    setHumanRequestedAt(now);
    setHandoffCollectingInfo(false);

    // Use collected name/phone from ref if available, otherwise extract from messages
    let name = pendingHandoffName.current;
    let phone = pendingHandoffPhone.current;
    pendingHandoffName.current = "";
    pendingHandoffPhone.current = "";

    if (!name || !phone) {
      const allText = messages.filter((m) => m.role === "user").map((m) => m.content).join(" \n");
      const extracted = extractContactInfo(allText);
      name = extracted.name || "";
      phone = extracted.phone || "";
    }

    const email = "";
    const transcript = messages.map((m) => `${m.role}: ${m.content}`).join("\n");
    const lastUserMsg = messages.filter((m) => m.role === "user").at(-1)?.content || "";

    // Save lead
    await saveLeadToDB(
      name || "Sales Chat Visitor",
      phone || "N/A",
      email,
      `[HANDOFF] ${reason}\nTranscript: ${transcript.slice(0, 400)}`
    );

    // Create support ticket
    try {
      const { data: ticket, error: tErr } = await supabase
        .from("support_tickets")
        .insert({
          issue: lastUserMsg.slice(0, 500) || "Sales Chat Handoff — " + reason.slice(0, 300),
          phone: phone || "N/A",
          status: "open",
          subject: `Sales Chat — ${name || "Visitor"} solicita hablar con persona`,
          source: "charvys",
          priority: "high",
          needs_human: true,
          conversation_summary: transcript.slice(0, 2000),
          client_id: null,
        })
        .select("id")
        .maybeSingle();

      if (ticket && !tErr) {
        const tid = ticket.id;
        const rows = messages.map((m) => ({
          ticket_id: tid,
          sender_role: m.role === "user" ? "client" : "admin",
          message: m.content.slice(0, 1000),
          read_by_admin: true,
          read_by_client: true,
        }));
        if (rows.length) await supabase.from("ip_support_messages").insert(rows.slice(-20));
        await supabase.from("ip_support_messages").insert({
          ticket_id: tid,
          sender_role: "client",
          message: "[CLIENT REQUESTED HUMAN] " + reason,
          read_by_admin: false,
          read_by_client: true,
        });
        setHandoffTicketId(tid);
        sessionStorage.setItem("ip_chat_handoff_ticket", String(tid));
      }
    } catch (err) {
      console.error("Handoff ticket error:", err);
    }

    setHandoffMode(true);

    // Doorbell!
    playDoorbell();

    // Personalized thank-you message with the client's name
    if (name) {
      addMessage({
        id: `handoff_${Date.now()}`,
        role: "system",
        content: `Gracias, ${name}. Ya te conecté con un asesor de Imagen Printing. En breve continuará la conversación contigo aquí mismo.`,
        createdAt: now,
      });
    } else {
      addMessage({
        id: `handoff_${Date.now()}`,
        role: "system",
        content: "🔔 Ya te conecté con un asesor de Imagen Printing. En breve continuará la conversación contigo aquí mismo.",
        createdAt: now,
      });
    }

    isHandlingRef.current = false;
  }, [messages, addMessage, saveLeadToDB]);

  /* ── Submit handoff info (name + phone form) ─────────────────── */
  const submitHandoffInfo = useCallback(async () => {
    const name = handoffFormName.trim();
    const phoneDigits = stripNonDigits(handoffFormPhone);

    if (!name) {
      setHandoffFormError("Por favor ingresa tu nombre.");
      return;
    }
    if (phoneDigits.length < 10) {
      setHandoffFormError("El teléfono debe tener al menos 10 dígitos.");
      return;
    }

    setHandoffFormError("");
    setHandoffCollectingInfo(false);

    // Add the collected info as a user-style status message in chat
    addMessage({
      id: `info_${Date.now()}`,
      role: "user",
      content: `Nombre: ${name}  |  Teléfono: ${handoffFormPhone.trim()}`,
      createdAt: new Date().toISOString(),
    });

    // Store the collected name/phone in a ref so handoffToHuman can use them
    pendingHandoffName.current = name;
    pendingHandoffPhone.current = handoffFormPhone.trim();

    // Now proceed to real handoff with collected data
    await handoffToHuman("Client requested human assistance");
  }, [handoffFormName, handoffFormPhone, addMessage, handoffToHuman]);

  /* ── Send to PHP AI endpoint ──────────────────────────────────────── */
  const sendToAI = useCallback(
    async (text: string, conversationHistory: ChatMessage[]) => {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
      const rulesText = rules
        .map((r) => `[${r.title}]\n${r.rule_text}`)
        .join("\n\n---\n\n");

      const history = buildHistory(conversationHistory);
      const context = extractKnownContext(conversationHistory);

      const body = {
        message: text,
        lang: navigator.language?.startsWith("es") ? "es" : "en",
        page_url: pageUrl,
        page_path: pagePath,
        session_id: sessionId,
        rules: rulesText,
        history,
        context,
      };

      console.log("[Charvys] /ai/chat.php PAYLOAD:", JSON.stringify(body, null, 2));

      try {
        const res = await fetch("https://imagenprinting.com/ai/chat.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        console.log("[Charvys] /ai/chat.php HTTP STATUS:", res.status, res.statusText);

        let data;
        const rawText = await res.text();
        try {
          data = JSON.parse(rawText);
        } catch {
          data = { _raw: rawText };
        }
        console.log("[Charvys] /ai/chat.php RESPONSE:", data);

        if (!res.ok) {
          console.error("[Charvys] /ai/chat.php HTTP ERROR:", res.status, data);
          throw new Error(`HTTP ${res.status}: ${data?.error || data?.message || rawText || "Unknown error"}`);
        }

        if (data.success === false) {
          console.error("[Charvys] /ai/chat.php API ERROR:", data);
          throw new Error(`API error: ${data?.error || data?.message || JSON.stringify(data)}`);
        }

        return {
          reply:
            data.reply ??
            data.message ??
            data.response ??
            data.text ??
            data.answer ??
            "",
          lead_captured: data.lead_captured ?? data.leadCaptured ?? false,
          actions: data.actions,
          requires_human_handoff: data.requires_human_handoff ?? data.action === "REQUEST_HUMAN",
        };
      } catch (err) {
        console.error("[Charvys] /ai/chat.php CATCH ERROR:", err);
        const isNetworkError =
          err instanceof TypeError &&
          (String(err.message).includes("Failed to fetch") ||
            String(err.message).includes("NetworkError"));
        return {
          reply: isNetworkError
            ? "The AI assistant is temporarily unavailable from this preview. It will work perfectly once your site is live on imagenprinting.com."
            : `Sorry, I could not connect to the assistant right now. (${err instanceof Error ? err.message : "Unknown error"})`,
          requires_human_handoff: false,
        };
      }
    },
    [rules, sessionId]
  );

  /* ─── Handle user submit ──────────────────────────────────────────── */
  const handleSubmit = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading || sendingHandoff || isHandlingRef.current) return;
    setInput("");

    // Handoff mode → send to ticket
    if (handoffMode) {
      await sendClientHandoffMessage(text);
      return;
    }

    // ── Sales Flow Mode: handle address input ──
    if (salesFlowActive && salesFlow.state.step === "shipping_address") {
      const userMsg: ChatMessage = {
        id: `u_${Date.now()}`,
        role: "user",
        content: text,
        createdAt: new Date().toISOString(),
      };
      addMessage(userMsg);
      salesFlow.processAddressInput(text);
      return;
    }

    // ── Detect product query → activate sales flow ──
    if (!salesFlowActive && isProductQuery(text) && !salesFlowTriggered) {
      const slug = detectProductFamily(text) || "business-cards";
      const qty = detectQuantity(text);
      const userMsg: ChatMessage = {
        id: `u_${Date.now()}`,
        role: "user",
        content: text,
        createdAt: new Date().toISOString(),
      };
      addMessage(userMsg);

      addMessage({
        id: `a_${Date.now()}`,
        role: "assistant",
        content: "Permíteme consultar nuestros precios actualizados...",
        createdAt: new Date().toISOString(),
      });

      activateSalesFlow(slug, qty ?? undefined);
      return;
    }

    const userMsg: ChatMessage = {
      id: `u_${Date.now()}`,
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
    };
    addMessage(userMsg);
    setLoading(true);

    const conversation = [...messages, userMsg];
    const aiResponse = await sendToAI(text, conversation);

    const aiMsg: ChatMessage = {
      id: `a_${Date.now()}`,
      role: "assistant",
      content: aiResponse.reply ?? "",
      createdAt: new Date().toISOString(),
      actions: aiResponse.actions,
    };
    addMessage(aiMsg);
    setLoading(false);
    setHasGreeted(true);

    // ✅ BACKEND-DRIVEN HANDOFF — only when backend explicitly signals
    if (aiResponse.requires_human_handoff && !humanRequested && !handoffMode) {
      const now = new Date().toISOString();
      setHumanRequested(true);
      setHumanRequestedAt(now);

      // Check if we already have name + phone from conversation
      const allKnown = conversation
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join(" \n");
      const known = extractContactInfo(allKnown);
      const knownPhoneDigits = stripNonDigits(known.phone || "");

      if (known.name.trim() && knownPhoneDigits.length >= 10) {
        // Already have valid info → handoff directly
        setHandoffCollectingInfo(false);
        pendingHandoffName.current = known.name.trim();
        pendingHandoffPhone.current = known.phone.trim();
        await handoffToHuman("Client requested human assistance");
      } else {
        // Need to collect info first — backend reply is already shown as aiMsg above
        setHandoffCollectingInfo(true);
        setHandoffFormName("");
        setHandoffFormPhone("");
        setHandoffFormError("");
      }
      return;
    }

    // Process AI actions
    if (aiResponse.actions && aiResponse.actions.length > 0) {
      const allUserText = messages
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join(" ");
      const { name, phone } = extractContactInfo(allUserText);

      for (const action of aiResponse.actions) {
        if (action.type === "ticket" && name.trim() && phone.trim()) {
          addMessage({
            id: `sys_${Date.now()}`,
            role: "assistant",
            content: "✅ Ticket de soporte creado. Nuestro equipo se pondrá en contacto contigo pronto.",
            createdAt: new Date().toISOString(),
          });
        }
        if (action.type === "lead" && (name.trim() || phone.trim())) {
          const need = allUserText.slice(0, 200);
          await submitLead(name.trim() || "AI Lead", phone.trim() || "", need);
        }
      }
    }

    // Backup lead capture
    if (aiResponse.lead_captured) {
      const allUserText = messages
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join(" ");
      const { name, phone } = extractContactInfo(allUserText);
      if (name.trim() || phone.trim()) {
        await submitLead(name.trim() || "AI Lead", phone.trim() || "", text.slice(0, 200));
      }
    }

    // Auto-capture lead when contact info detected
    const contact = extractContactInfo([...conversation, aiMsg].map((m) => m.content).join(" "));
    if (contact.name && contact.phone) {
      const allUserMsgs = conversation.filter((m) => m.role === "user").map((m) => m.content).join(" | ");
      await saveLeadToDB(contact.name, contact.phone, contact.email, `Chat: ${allUserMsgs.slice(0, 300)}`);
    }
  }, [input, loading, sendingHandoff, handoffMode, humanRequested, messages, addMessage, sendToAI, submitLead, saveLeadToDB, handoffToHuman]);

  /* ─── Keyboard ─────────────────────────────────────────────────────── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  /* ─── Quick messages ───────────────────────────────────────────────── */
  const sendQuickMessage = useCallback((text: string) => {
    handleSubmit(text);
  }, [handleSubmit]);

  /* ─── Send handoff message ─────────────────────────────────────────── */
  const sendClientHandoffMessage = async (text: string) => {
    if (!text.trim() || !handoffTicketId || sendingHandoff) return;
    setSendingHandoff(true);
    addMessage({ id: `u_${Date.now()}`, role: "user", content: text, createdAt: new Date().toISOString() });
    try {
      await supabase.from("ip_support_messages").insert({
        ticket_id: handoffTicketId,
        sender_role: "client",
        message: text.slice(0, 1000),
        read_by_admin: false,
        read_by_client: true,
      });
      await supabase.from("support_tickets").update({
        last_message_at: new Date().toISOString(),
        unread_admin: 1,
      }).eq("id", handoffTicketId);
    } catch (err) { console.error("Handoff send error:", err); }
    setSendingHandoff(false);
  };

  /* ─── Reset ────────────────────────────────────────────────────────── */
  const resetChat = () => {
    setMessages([]);
    setHasGreeted(false);
    setInput("");
    setHandoffMode(false);
    setHandoffTicketId(null);
    setLastAdminMsgId(0);
    setHumanRequested(false);
    setHumanRequestedAt(null);
    setHandoffCollectingInfo(false);
    setHandoffFormName("");
    setHandoffFormPhone("");
    setHandoffFormError("");
    setSalesFlowActive(false);
    setSalesFlowTriggered(false);
    setDetectedProductSlug("business-cards");
    pendingHandoffName.current = "";
    pendingHandoffPhone.current = "";
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem("ip_chat_handoff_ticket");
  };

  /* ─── Quick actions ────────────────────────────────────────────────── */
  const quickActions = [
    { label: "Cotizar producto", icon: "ri-price-tag-3-line", action: () => {
      if (!salesFlowActive && !salesFlowTriggered) {
        addMessage({
          id: `u_${Date.now()}`,
          role: "user",
          content: "Quiero cotizar un producto",
          createdAt: new Date().toISOString(),
        });
        addMessage({
          id: `a_${Date.now()}`,
          role: "assistant",
          content: "¡Claro! ¿Qué producto te interesa? Tenemos tarjetas de presentación, flyers, banners, stickers, camisetas, DTF, yard signs y más.",
          createdAt: new Date().toISOString(),
        });
      }
    }},
    { label: "Recomiéndame", icon: "ri-lightbulb-line", text: "¿Qué producto me recomiendas para mi negocio?" },
    { label: "Precios", icon: "ri-money-dollar-circle-line", text: "¿Cuáles son sus precios?" },
  ];

  return (
    <>
      <div id={WIDGET_ID} className="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-[9998] font-sans">
        {/* ── Floating Button ──────────────────────────────────────── */}
        <button
          onClick={() => setOpen((p) => !p)}
          className="relative group cursor-pointer"
          aria-label={open ? "Cerrar Chat de Ventas" : "Abrir Chat de Ventas"}
        >
          <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
            open
              ? "bg-[#111111] border-2 border-[#d4af37]/40 scale-90"
              : "bg-[#d4af37] hover:bg-[#c9a227] hover:scale-105"
          }`}>
            {open ? (
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-close-line text-[#d4af37] text-xl" />
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-robot-2-line text-black text-xl" />
                </div>
                <span className="text-[7px] font-black text-black tracking-widest mt-0.5">CHARVYS</span>
              </div>
            )}
          </div>
          {!open && (
            <div className="absolute inset-0 rounded-full bg-[#d4af37]/30 animate-ping pointer-events-none" style={{ animationDuration: "2.5s" }} />
          )}
          {!open && (
            <div className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white z-10 ${
              rulesLoaded ? "bg-green-400" : "bg-red-400"
            }`} />
          )}
        </button>

        {/* ── Chat Panel ────────────────────────────────────────────── */}
        {open && (
          <>
            {/* Mobile backdrop */}
            {isMobile && (
              <div
                className="fixed inset-0 bg-black/50 z-[9998]"
                onClick={() => setOpen(false)}
              />
            )}

            <div
              className={
                isMobile
                  // MOBILE ONLY: fullscreen
                  ? "fixed inset-0 w-screen h-dvh max-w-none max-h-none rounded-none z-[9999] bg-[#0f0f0f] overflow-hidden flex flex-col"
                  // DESKTOP ALWAYS: floating panel (v953 identical)
                  : "absolute bottom-[80px] right-0 md:bottom-[88px] md:right-0 w-[calc(100vw-2rem)] max-w-[380px] bg-[#0f0f0f] rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col"
              }
              style={isMobile ? {} : { height: "min(560px, calc(100dvh - 140px))" }}
            >
              {/* Header */}
              <div className="bg-[#1a1a1a] px-4 py-3.5 flex items-center gap-3 flex-shrink-0 border-b border-white/8">
                <div className="w-10 h-10 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/25 flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-robot-2-line text-[#d4af37] text-lg" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-bold text-white">Charvys</p>
                    <span className="px-1.5 py-0.5 rounded-md bg-[#d4af37]/15 text-[#d4af37] text-[9px] font-black tracking-widest">AI</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {handoffCollectingInfo ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <span className="text-[10px] text-amber-400/80 font-medium">Completa tus datos</span>
                      </>
                    ) : humanRequested ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <span className="text-[10px] text-amber-400/80 font-medium">Conectando con asesor...</span>
                      </>
                    ) : rulesLoaded ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        <span className="text-[10px] text-white/45 font-medium">Agente de Ventas</span>
                      </>
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <span className="text-[10px] text-white/45 font-medium">Cargando...</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={toggleMute}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/8 transition cursor-pointer ${muted ? "text-red-400 hover:text-red-300" : "text-white/40 hover:text-white/70"}`}
                    title={muted ? "Activar sonido" : "Silenciar notificaciones"}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={muted ? "ri-volume-mute-line text-sm" : "ri-volume-up-line text-sm"} />
                    </div>
                  </button>
                  <button
                    onClick={resetChat}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/40 hover:text-white/70 transition cursor-pointer"
                    title="Nueva conversación"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-refresh-line text-sm" />
                    </div>
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/40 hover:text-white/70 transition cursor-pointer"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-close-line text-sm" />
                    </div>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#0a0a0a]">
                {messages.length === 0 && !hasGreeted && (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-16 h-16 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center mb-4">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-robot-2-line text-[#d4af37] text-2xl" />
                      </div>
                    </div>
                    <h3 className="text-white font-bold text-base mb-1">Charvys AI</h3>
                    <p className="text-white/50 text-xs mb-1">Agente de Ventas</p>
                    <p className="text-white/35 text-xs leading-relaxed">
                      ¡Hola! Soy tu asesor virtual de Imagen Printing. Te ayudo a cotizar, elegir productos y resolver dudas.
                    </p>
                    <p className="text-white/25 text-[11px] mt-3">
                      ¿En qué te puedo ayudar hoy?
                    </p>
                  </div>
                )}

                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role !== "user" && (
                      <div className="w-7 h-7 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/20 flex items-center justify-center flex-shrink-0 mr-1.5 self-end">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          {msg.role === "system" ? (
                            <i className="ri-notification-3-line text-[#d4af37] text-[10px]" />
                          ) : (
                            <i className="ri-robot-2-line text-[#d4af37] text-[10px]" />
                          )}
                        </div>
                      </div>
                    )}
                    <div className={`max-w-[82%] flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}>
                      <div
                        className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "bg-[#d4af37] text-black rounded-br-md font-medium"
                            : msg.role === "system"
                            ? "bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-bl-md"
                            : "bg-[#1a1a1a] text-white/80 border border-white/8 rounded-bl-md"
                        }`}
                      >
                        {msg.content}
                      </div>
                      <span className="text-[10px] text-white/25 mt-1 px-1">
                        {formatTime(msg.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}

                {/* ── Sales Flow Messages ── */}
                {salesFlowActive && salesFlow.messages.map((sfMsg) => (
                  <div key={sfMsg.id} className="flex justify-start">
                    <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mr-1.5 self-end">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-robot-2-line text-emerald-400 text-[10px]" />
                      </div>
                    </div>
                    <div className="max-w-[85%]">
                      <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-2xl rounded-bl-md px-3.5 py-2.5">
                        <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{sfMsg.content}</p>
                        {sfMsg.options && sfMsg.options.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {sfMsg.options.map((opt) => {
                              // ── Payment buttons (Square / PayPal) ──
                              if (opt.key === "pay_square") {
                                return (
                                  <button
                                    key={opt.key}
                                    onClick={() => salesFlow.payWithSquare()}
                                    disabled={salesFlow.loading}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-600 text-white text-[12px] font-semibold hover:bg-slate-700 transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <i className="ri-layout-grid-line text-sm"></i>
                                    {opt.label}
                                  </button>
                                );
                              }
                              if (opt.key === "pay_paypal") {
                                return (
                                  <button
                                    key={opt.key}
                                    onClick={() => salesFlow.payWithPayPal()}
                                    disabled={salesFlow.loading}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 text-white text-[12px] font-semibold hover:bg-sky-700 transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <i className="ri-paypal-line text-sm"></i>
                                    {opt.label}
                                  </button>
                                );
                              }
                              // ── Fallback: open Square link if popup blocked ──
                              if (opt.key === "open_square") {
                                return (
                                  <a
                                    key={opt.key}
                                    href={salesFlow.paymentUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-600 text-white text-[12px] font-semibold hover:bg-slate-700 transition cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-external-link-line text-sm"></i>
                                    {opt.label}
                                  </a>
                                );
                              }
                              // ── Fallback: open PayPal link if popup blocked ──
                              if (opt.key === "open_paypal") {
                                return (
                                  <a
                                    key={opt.key}
                                    href={salesFlow.paymentUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 text-white text-[12px] font-semibold hover:bg-sky-700 transition cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-external-link-line text-sm"></i>
                                    {opt.label}
                                  </a>
                                );
                              }
                              if (opt.key === "pay_now") {
                                return (
                                  <button
                                    key={opt.key}
                                    onClick={() => salesFlow.handlePayNow()}
                                    disabled={salesFlow.loading}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-[12px] font-semibold hover:bg-emerald-600 transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {opt.icon && <i className={`${opt.icon} text-sm`}></i>}
                                    {opt.label}
                                  </button>
                                );
                              }
                              if (opt.key === "confirm_address") {
                                return (
                                  <button
                                    key={opt.key}
                                    onClick={() => salesFlow.confirmAddress(true)}
                                    disabled={salesFlow.loading}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white text-[12px] font-semibold hover:bg-emerald-600 transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {opt.icon && <i className={`${opt.icon} text-sm`}></i>}
                                    {opt.label}
                                  </button>
                                );
                              }
                              if (opt.key === "fix_address") {
                                return (
                                  <button
                                    key={opt.key}
                                    onClick={() => salesFlow.confirmAddress(false)}
                                    disabled={salesFlow.loading}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-500/30 text-emerald-400 text-[12px] font-medium hover:bg-emerald-500/10 transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {opt.icon && <i className={`${opt.icon} text-sm`}></i>}
                                    {opt.label}
                                  </button>
                                );
                              }
                              return (
                                <button
                                  key={opt.key}
                                  onClick={() => salesFlow.selectOption(opt.key)}
                                  disabled={salesFlow.loading}
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-500/30 text-emerald-400 text-[12px] font-medium hover:bg-emerald-500/10 transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {opt.icon && <i className={`${opt.icon} text-sm`}></i>}
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] text-white/20 mt-1 px-1 inline-block">
                        {formatTime(new Date(sfMsg.timestamp).toISOString())}
                      </span>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/20 flex items-center justify-center flex-shrink-0 mr-1.5 self-end">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-robot-2-line text-[#d4af37] text-[10px]" />
                      </div>
                    </div>
                    <div className="bg-[#1a1a1a] border border-white/8 rounded-2xl rounded-bl-md px-3.5 py-2.5">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Human Request Banner — only show after actual handoff, not during info collection */}
              {humanRequested && !handoffCollectingInfo && (
                <div className="mx-3 mb-1 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center gap-2.5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-voice-line text-amber-400 text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-amber-300 text-xs font-semibold">Asesor en camino</p>
                    <p className="text-amber-400/60 text-[10px]">Un asesor de Imagen Printing te atenderá en breve.</p>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center animate-pulse">
                    <i className="ri-message-3-line text-amber-400 text-xs" />
                  </div>
                </div>
              )}

              {/* Quick Actions — hide during handoff or info collection */}
              {!humanRequested && !handoffCollectingInfo && messages.length <= 2 && (
                <div className="px-3 pt-1.5 pb-1 flex gap-2 flex-wrap bg-[#0a0a0a] border-t border-white/5">
                  {quickActions.map((qa) => (
                    <button
                      key={qa.label}
                      onClick={() => {
                        if (qa.action) {
                          qa.action();
                        } else if (qa.text) {
                          sendQuickMessage(qa.text);
                        }
                      }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-[#d4af37] bg-[#d4af37]/8 hover:bg-[#d4af37]/15 rounded-lg transition cursor-pointer whitespace-nowrap border border-[#d4af37]/15"
                    >
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${qa.icon} text-[10px]`} />
                      </div>
                      {qa.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Input — switches between normal chat input and handoff name/phone form */}
              <div className="p-3 bg-[#0f0f0f] border-t border-white/8 flex-shrink-0">
                {handoffCollectingInfo ? (
                  /* ── Name + Phone Collection Form ─── */
                  <div className="space-y-2.5">
                    <input
                      type="text"
                      placeholder="Tu nombre"
                      value={handoffFormName}
                      onChange={(e) => { setHandoffFormName(e.target.value); setHandoffFormError(""); }}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/40 focus:bg-white/8 transition-all caret-[#d4af37]"
                      autoFocus
                    />
                    <input
                      type="tel"
                      placeholder="Tu teléfono (mínimo 10 dígitos)"
                      value={handoffFormPhone}
                      onChange={(e) => { setHandoffFormPhone(e.target.value); setHandoffFormError(""); }}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/40 focus:bg-white/8 transition-all caret-[#d4af37]"
                    />
                    {handoffFormError && (
                      <p className="text-red-400/80 text-[11px] px-1">{handoffFormError}</p>
                    )}
                    <button
                      onClick={submitHandoffInfo}
                      disabled={!handoffFormName.trim() || !handoffFormPhone.trim()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#d4af37] hover:bg-[#c9a227] disabled:opacity-30 text-black font-semibold text-sm rounded-xl transition cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-voice-line text-sm" />
                      </div>
                      Conectarme con asesor
                    </button>
                    <p className="text-center text-white/20 text-[10px]">
                      Tus datos solo se usan para contactarte.
                    </p>
                  </div>
                ) : (
                  /* ── Normal Chat Input ─── */
                  <>
                    <div className="flex gap-2 items-end">
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value.slice(0, 500))}
                        onKeyDown={handleKeyDown}
                        placeholder={humanRequested ? "Escribe tu mensaje para el asesor..." : "Escribe tu mensaje..."}
                        rows={1}
                        disabled={loading}
                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/25 resize-none focus:outline-none focus:border-[#d4af37]/40 focus:bg-white/8 transition-all min-h-[42px] max-h-[110px] disabled:opacity-50 caret-[#d4af37]"
                      />
                      <button
                        onClick={() => handleSubmit()}
                        disabled={!input.trim() || loading}
                        className="w-10 h-10 flex items-center justify-center bg-[#d4af37] hover:bg-[#c9a227] disabled:opacity-30 text-black rounded-xl transition cursor-pointer flex-shrink-0"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-send-plane-fill text-base" />
                        </div>
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-white/20">{input.length}/500</span>
                      {humanRequested ? (
                        <span className="flex items-center gap-1 px-3 py-1 rounded-lg text-[11px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-400/60 cursor-default whitespace-nowrap">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-user-voice-line text-[10px]" />
                          </div>
                          Asesor notificado
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          {whatsappUrl && (
                            <a
                              href={whatsappUrl}
                              target="_blank"
                              rel="nofollow noreferrer"
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 transition cursor-pointer whitespace-nowrap"
                            >
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-whatsapp-line text-[10px]" />
                              </div>
                              WhatsApp
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}

/* ─── Gate de montaje: no montar el motor en rutas ocultas ─────────────── */
export default function AIChatWidget() {
  const location = useLocation();
  if (!shouldShowWidget(location.pathname)) {
    return null;
  }
  return <AIChatWidgetInner />;
}